import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Trash2, ArrowLeft, Save, Search, Plus, ArrowRight, Calculator, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';

const ParametreFT = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const { ftId, exclusionId, typeCible, quantiteBase } = location.state || {};
    const effectiveFtId = ftId || id;
    
    const [lignes, setLignes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [selectedIngredient, setSelectedIngredient] = useState(null);
    const [quantiteSaisie, setQuantiteSaisie] = useState(1);
    const [montantSaisie, setMontantSaisie] = useState(0); 
    
    const [produitsCibles, setProduitsCibles] = useState([]);
    const [selectedProduitCible, setSelectedProduitCible] = useState(exclusionId || '');
    const [quantiteBaseRecette, setQuantiteBaseRecette] = useState(quantiteBase || 1); // Gestion de la quantité de base
    const [ingredientsDisponibles, setIngredientsDisponibles] = useState([]);
    const [coutTotalFiche, setCoutTotalFiche] = useState(0);
    const [coutUnitaireReel, setCoutUnitaireReel] = useState(0);

    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success', 
        message: '',
        onConfirm: null
    });

    const COLORS = {
        bordeaux: '#800000',
        bordeauxLight: '#fef2f2',
        anthracite: '#1e293b',
        success: '#059669',
        danger: '#dc2626',
        border: '#e2e8f0'
    };

    const s = {
        container: { display: 'flex', height: '100vh', background: 'white' },
        mainArea: { flex: 1, display: 'grid', gridTemplateColumns: '1.2fr 1fr', background: '#f8fafc', position: 'relative' },
        leftCol: { padding: '20px', display: 'flex', flexDirection: 'column', borderRight: `2px solid ${COLORS.border}`, overflow: 'hidden' },
        rightCol: { display: 'flex', flexDirection: 'column', background: '#f1f5f9' },
        headerPanier: { background: COLORS.bordeaux, color: 'white', padding: '15px 20px', fontWeight: '800', textAlign: 'center', fontSize: '13px', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        saisieSection: { padding: '15px 20px', display: 'flex', flexDirection: 'column', background: 'white', borderBottom: `2px solid ${COLORS.border}` },
        qtyRow: { display: 'flex', padding: '0 20px', gap: '10px', alignItems: 'flex-end' },
        inputQty: { flex: 1, padding: '15px', fontSize: '24px', fontWeight: '900', textAlign: 'center', border: `2px solid ${COLORS.bordeaux}`, borderRadius: '8px', outline: 'none' },
        inputAmount: { flex: 1, padding: '15px', fontSize: '24px', fontWeight: '900', textAlign: 'center', border: `2px solid ${COLORS.success}`, borderRadius: '8px', outline: 'none', background: '#f0fdf4', color: COLORS.success },
        btnAjouter: { padding: '0 20px', height: '60px', background: COLORS.bordeaux, color: 'white', border: 'none', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', fontSize: '14px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
        listeSection: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' },
        table: { width: '100%', borderCollapse: 'collapse' },
        thead: { background: COLORS.anthracite, color: 'white', position: 'sticky', top: 0, zIndex: 10 },
        footer: { padding: '15px', background: 'white', borderTop: `2px solid ${COLORS.border}`, display: 'flex', gap: '10px' },
        selectedRow: { background: COLORS.bordeauxLight, borderLeft: `4px solid ${COLORS.bordeaux}` },
        toastContainer: {
            position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
            zIndex: 1000, display: 'flex', alignItems: 'center',
            background: 'white', padding: '12px 20px', borderRadius: '15px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            border: `1px solid ${COLORS.border}`,
            minWidth: '400px',
            transition: 'all 0.3s ease'
        },
        confirmBtn: { background: COLORS.bordeaux, color: 'white', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' },
        cancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }
    };

    useEffect(() => {
        if (notification.isOpen && notification.type !== 'confirm') {
            const timer = setTimeout(() => setNotification(prev => ({ ...prev, isOpen: false })), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification.isOpen, notification.type]);

    const showToast = (message, type = 'success') => {
        setNotification({ isOpen: true, type, message, onConfirm: null });
    };

    const showConfirm = (message, onConfirm) => {
        setNotification({ isOpen: true, type: 'confirm', message, onConfirm });
    };

    // Calculs dynamiques du coût total du lot et du coût unitaire par rapport à la quantité de base
    useEffect(() => {
        const total = lignes.reduce((acc, ligne) => acc + (Number(ligne.prix_unitaire) * Number(ligne.quantite)), 0);
        setCoutTotalFiche(Math.round(total));
        
        const qBase = Number(quantiteBaseRecette) > 0 ? Number(quantiteBaseRecette) : 1;
        setCoutUnitaireReel(total / qBase);
    }, [lignes, quantiteBaseRecette]);

    useEffect(() => {
        fetchData();
    }, [effectiveFtId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [mpRes, psfRes, pfRes] = await Promise.all([
                API.get('/raw-materials').catch(() => ({ data: [] })),
                API.get('/semi-finished-products').catch(() => ({ data: [] })),
                API.get('/products').catch(() => ({ data: [] }))
            ]);
            const allIngredients = [
                ...(mpRes.data || []).filter(m => m.is_active === 1).map(m => ({ 
                    id: m.id, nom: m.nom, type: 'MP', unite: m.unite_mesure || 'U', prix_unitaire: Number(m.cmp) || 0 
                })),
                ...(psfRes.data || []).filter(p => p.is_active === 1).map(p => ({ 
                    id: p.id, nom: p.nom, type: 'PSF', unite: p.unite_mesure || 'U', prix_unitaire: Number(p.cmp) || 0 
                }))
            ].filter(item => item.id !== exclusionId);

            setIngredientsDisponibles(allIngredients);
            setProduitsCibles([
                ...(pfRes.data || []).filter(f => f.is_active === 1).map(f => ({ id: f.id, nom: `PF: ${f.nom}` })),
                ...(psfRes.data || []).filter(p => p.is_active === 1).map(s => ({ id: s.id, nom: `PSF: ${s.nom}` }))
            ]);

            if (effectiveFtId) {
                try {
                    const ftDetails = await API.get(`/technical-sheets/${effectiveFtId}`);
                    const data = ftDetails.data;
                    if (data.product_id || data.semi_fini_id) {
                        setSelectedProduitCible(data.product_id || data.semi_fini_id);
                    }
                    if (data.quantite_base) {
                        setQuantiteBaseRecette(data.quantite_base);
                    }
                    if (data.items && Array.isArray(data.items)) {
                        const mappedLignes = data.items.map(l => {
                            const ingredientId = l.matiere_id || l.composant_semi_fini_id;
                            const fallbackInfo = allIngredients.find(ing => ing.id === ingredientId);
                            return {
                                id: `LIG-${Math.random().toString(36).substr(2, 9)}`,
                                ingredient_id: ingredientId,
                                nom: l.designation || 'Article inconnu',
                                type: !!l.composant_semi_fini_id ? 'PSF' : 'MP',
                                unite: l.unite || 'U',
                                prix_unitaire: Number(l.prix_unitaire) || (fallbackInfo ? fallbackInfo.prix_unitaire : 0),
                                quantite: Number(l.quantite_requise || l.quantite || 0)
                            };
                        });
                        setLignes(mappedLignes);
                    }
                } catch (apiErr) {
                    console.warn("Fiche introuvable ou obsolète, initialisation d'une configuration propre.", apiErr);
                    if (exclusionId) setSelectedProduitCible(exclusionId);
                }
            }
        } catch (err) {
            showToast("Impossible de charger les données", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleQtyChange = (val) => {
        const qte = parseFloat(val || 0);
        setQuantiteSaisie(val);
        if (selectedIngredient) {
            setMontantSaisie(Math.round(qte * selectedIngredient.prix_unitaire));
        }
    };

    const handleAmountChange = (val) => {
        const montant = parseFloat(val || 0);
        setMontantSaisie(val);
        if (selectedIngredient && selectedIngredient.prix_unitaire > 0) {
            const calculQte = montant / selectedIngredient.prix_unitaire;
            setQuantiteSaisie(Number(calculQte.toFixed(4)));
        }
    };

    useEffect(() => {
        if (selectedIngredient) {
            setMontantSaisie(Math.round(quantiteSaisie * selectedIngredient.prix_unitaire));
        }
    }, [selectedIngredient]);

    const confirmAddLine = () => {
        if (!selectedIngredient) return showToast("Veuillez sélectionner un article", "error");
        if (quantiteSaisie <= 0) return showToast("La quantité doit être supérieure à 0", "error");
        if (lignes.find(l => l.ingredient_id === selectedIngredient.id)) return showToast("Cet article figure déjà dans la recette", "error");
        
        const newLine = {
            id: `LIG-${Date.now()}`,
            ingredient_id: selectedIngredient.id,
            nom: selectedIngredient.nom,
            type: selectedIngredient.type,
            unite: selectedIngredient.unite,
            prix_unitaire: selectedIngredient.prix_unitaire,
            quantite: parseFloat(quantiteSaisie)
        };
        
        setLignes([...lignes, newLine]);
        setSelectedIngredient(null);
        setQuantiteSaisie(1);
        setMontantSaisie(0);
    };

    const handleSave = async () => {
        if (!selectedProduitCible) return showToast("Sélectionnez un produit de sortie (cible)", "error");
        if (lignes.length === 0) return showToast("Ajoutez au moins un ingrédient", "error");
        
        try {
            const itemsFormates = lignes.map(l => ({
                id: l.id,
                type: l.type, 
                matiere_id: l.type === 'MP' ? l.ingredient_id : null,
                composant_semi_fini_id: l.type === 'PSF' ? l.ingredient_id : null,
                quantite_requise: l.quantite,
                unite: l.unite,
                company_id: "COMP-01" 
            }));

            // Payload incluant la quantité de base (rendement du lot)
            const payload = { 
                ft_id: effectiveFtId, 
                cible_id: selectedProduitCible, 
                quantite_base: parseFloat(quantiteBaseRecette) || 1, 
                items: itemsFormates 
            };

            const response = await API.post('/technical-sheets/save-config', payload);
            
            if (response.data.success || response.status === 200) {
                showToast("Fiche technique enregistrée avec succès", "success");
                setTimeout(() => navigate('/analytique/ft/creation'), 1500);
            }
        } catch (err) { 
            console.error("Échec de l'enregistrement :", err.response || err);
            showToast(err.response?.data?.error || "Échec de l'enregistrement", "error"); 
        }
    };

    const NotificationToast = () => {
        if (!notification.isOpen) return null;
        const isConfirm = notification.type === 'confirm';
        const color = notification.type === 'error' ? COLORS.danger : (isConfirm ? '#f59e0b' : COLORS.success);
        return (
            <div style={{ ...s.toastContainer, borderLeft: `8px solid ${color}` }}>
                <div style={{ 
                    display: 'flex', justifyContent: 'center', alignItems: 'center', 
                    width: '35px', height: '35px', borderRadius: '50%', 
                    background: `${color}15`, marginRight: '15px', flexShrink: 0
                }}>
                    {notification.type === 'error' ? <X size={20} color={color} /> : 
                     isConfirm ? <AlertTriangle size={20} color={color} /> : 
                     <CheckCircle2 size={20} color={color} />}
                </div>
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: '700', color: COLORS.anthracite, fontSize: '14px' }}>{notification.message}</p>
                    {isConfirm && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button onClick={() => { notification.onConfirm(); setNotification({isOpen: false}); }} style={s.confirmBtn}>CONFIRMER</button>
                            <button onClick={() => setNotification({isOpen: false})} style={s.cancelBtn}>ANNULER</button>
                        </div>
                    )}
                </div>
                {!isConfirm && (
                    <button onClick={() => setNotification({isOpen: false})} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1' }}>
                        <X size={20} />
                    </button>
                )}
            </div>
        );
    };

    if (loading) return <div style={{padding: '40px', textAlign: 'center', fontWeight: '800', color: COLORS.bordeaux}}>CHARGEMENT DES PARAMÈTRES...</div>;

    return (
        <div style={s.container}>
            <Sidebar />
            <main style={s.mainArea}>
                <NotificationToast />
                
                {/* Colonne de gauche : Ingrédients disponibles */}
                <section style={s.leftCol}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                        <button onClick={() => navigate(-1)} style={{ background: '#f1f5f9', border: 'none', padding: '8px', borderRadius: '50%', cursor: 'pointer' }}><ArrowLeft size={18}/></button>
                        <div>
                            <h1 style={{ fontSize: '16px', fontWeight: '900', margin: 0, color: COLORS.anthracite }}>INGRÉDIENTS & COMPOSANTS</h1>
                            <p style={{ fontSize: '11px', color: '#64748b', fontWeight: '700' }}>DISPONIBLES EN STOCK</p>
                        </div>
                    </div>
                    <div style={{ position: 'relative', marginBottom: '15px' }}>
                        <Search style={{ position: 'absolute', right: '12px', top: '12px', color: '#94a3b8' }} size={20} />
                        <input 
                            style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '600', outlineColor: COLORS.bordeaux }} 
                            placeholder="Rechercher un composant..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white' }}>
                        <table style={s.table}>
                            <thead style={s.thead}>
                                <tr>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px' }}>DÉSIGNATION</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '11px' }}>UNITÉ</th>
                                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '11px' }}>PRIX UN.</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '11px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {ingredientsDisponibles
                                    .filter(i => i.nom.toLowerCase().includes(searchTerm.toLowerCase()))
                                    .map(item => (
                                    <tr key={item.id} onClick={() => setSelectedIngredient(item)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', ...(selectedIngredient?.id === item.id ? s.selectedRow : {}) }}>
                                        <td style={{ padding: '12px' }}>
                                            <div style={{ fontWeight: '800', fontSize: '13px' }}>{item.nom}</div>
                                            <div style={{ fontSize: '10px', color: COLORS.bordeaux, fontWeight: '900' }}>{item.type}</div>
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center', fontWeight: '700', color: '#64748b', fontSize: '12px' }}>{item.unite}</td>
                                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: '900', color: COLORS.success, fontSize: '14px' }}>{Math.round(item.prix_unitaire).toLocaleString()}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}><ArrowRight size={16} color={selectedIngredient?.id === item.id ? COLORS.bordeaux : '#cbd5e1'}/></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Colonne de droite : Paramétrage de la recette */}
                <section style={s.rightCol}>
                    <div style={s.headerPanier}>
                        <span>DÉTAILS DE LA RECETTE</span>
                        <div style={{ display: 'flex', gap: '15px', fontSize: '12px' }}>
                            <span>Lot : <strong style={{ color: '#fbbf24' }}>{coutTotalFiche.toLocaleString()} F</strong></span>
                            <span>Unitaire : <strong style={{ color: '#34d399' }}>{Math.round(coutUnitaireReel).toLocaleString()} F</strong></span>
                        </div>
                    </div>
                    
                    <div style={s.saisieSection}>
                        {/* Ligne : Produit Cible + Quantité de Base (Rendement du Lot) */}
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', padding: '0 20px 15px 20px' }}>
                            <div>
                                <label style={{ fontSize: '11px', fontWeight: '900', color: '#64748b' }}>PRODUIT CIBLE (SORTIE)</label>
                                <select disabled={!!exclusionId} style={{ width: '100%', padding: '12px', marginTop: '5px', borderRadius: '8px', border: `2px solid ${COLORS.bordeaux}`, fontWeight: '800', background: '#f8fafc', color: COLORS.bordeaux }} value={selectedProduitCible} onChange={(e) => setSelectedProduitCible(e.target.value)}>
                                    <option value="">--- Sélectionner le produit ---</option>
                                    {produitsCibles.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: '11px', fontWeight: '900', color: COLORS.success }}>QTÉ DE BASE (RENDEMENT)</label>
                                <input 
                                    type="number" 
                                    step="any" 
                                    min="0.0001" 
                                    style={{ width: '100%', padding: '12px', marginTop: '5px', borderRadius: '8px', border: `2px solid ${COLORS.success}`, fontWeight: '900', background: '#f0fdf4', color: COLORS.success, textAlign: 'center' }} 
                                    value={quantiteBaseRecette} 
                                    onChange={(e) => setQuantiteBaseRecette(e.target.value)} 
                                />
                            </div>
                        </div>

                        <div style={{ padding: '12px 20px', background: selectedIngredient ? COLORS.bordeauxLight : '#f1f5f9', margin: '0 20px 15px 20px', borderRadius: '8px', border: selectedIngredient ? `2px solid ${COLORS.bordeaux}` : '2px dashed #cbd5e1' }}>
                            <span style={{fontSize: '10px', fontWeight: '900', color: COLORS.bordeaux}}>AJOUTER À LA RECETTE :</span>
                            <div style={{fontSize: '18px', fontWeight: '900', color: COLORS.anthracite, display: 'flex', justifyContent: 'space-between'}}>
                                <span>{selectedIngredient ? selectedIngredient.nom : "SÉLECTIONNEZ UN ARTICLE"}</span>
                                {selectedIngredient && <span style={{fontSize: '14px', color: '#64748b'}}>{selectedIngredient.unite}</span>}
                            </div>
                        </div>

                        <div style={s.qtyRow}>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '11px', fontWeight: '900', color: '#64748b' }}>QUANTITÉ POUR LE LOT ({quantiteBaseRecette} UNITÉS)</label>
                                <input type="number" style={s.inputQty} value={quantiteSaisie} onChange={(e) => handleQtyChange(e.target.value)} min="0.0001" step="0.0001" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '11px', fontWeight: '900', color: COLORS.success }}>COÛT ESTIMÉ (FCFA)</label>
                                <input type="number" style={s.inputAmount} value={montantSaisie} onChange={(e) => handleAmountChange(e.target.value)} />
                            </div>
                            <button onClick={confirmAddLine} style={{ ...s.btnAjouter, opacity: selectedIngredient ? 1 : 0.5, cursor: selectedIngredient ? 'pointer' : 'not-allowed' }}><Plus size={20}/><span>AJOUTER</span></button>
                        </div>
                    </div>

                    <div style={s.listeSection}>
                        <table style={s.table}>
                            <thead style={{ background: COLORS.bordeaux, color: 'white' }}>
                                <tr>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '11px' }}>COMPOSANT</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '11px' }}>QTÉ (POUR {quantiteBaseRecette} UNITÉS)</th>
                                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '11px' }}>COÛT</th>
                                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '11px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {lignes.map((ligne) => (
                                    <tr key={ligne.id} style={{ borderBottom: '1px solid #cbd5e1', background: 'white' }}>
                                        <td style={{ padding: '12px' }}>
                                            <div style={{fontWeight: '800', fontSize: '13px'}}>{ligne.nom}</div>
                                            <div style={{fontSize: '10px', color: '#64748b'}}>{Math.round(ligne.prix_unitaire).toLocaleString()} / {ligne.unite}</div>
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center', fontWeight: '900', color: COLORS.bordeaux, fontSize: '15px' }}>{ligne.quantite} <span style={{fontSize: '10px'}}>{ligne.unite}</span></td>
                                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: '900', color: COLORS.success }}>{Math.round(ligne.prix_unitaire * ligne.quantite).toLocaleString()}</td>
                                        <td style={{ padding: '12px', textAlign: 'right' }}>
                                            <button onClick={() => showConfirm(`Retirer ${ligne.nom} de la fiche ?`, () => setLignes(lignes.filter(l => l.id !== ligne.id)))} style={{ color: '#ef4444', border: 'none', background: '#fee2e2', padding: '6px', borderRadius: '5px', cursor: 'pointer' }}><Trash2 size={16} /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {lignes.length === 0 && (
                            <div style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', padding: '40px'}}><Calculator size={48} strokeWidth={1} /><p style={{fontWeight: '800', fontSize: '12px', marginTop: '10px'}}>AUCUN COMPOSANT DANS LA RECETTE</p></div>
                        )}
                    </div>

                    <div style={s.footer}>
                        <button style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '15px 25px', borderRadius: '8px', fontWeight: '800', cursor: 'pointer' }} onClick={() => showConfirm("Voulez-vous vider toute la liste ?", () => setLignes([]))}>REINITIALISER</button>
                        <button onClick={handleSave} style={{ flex: 1, background: COLORS.success, color: 'white', border: 'none', padding: '15px', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><Save size={20} /> ENREGISTRER</button>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default ParametreFT;